import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

export default function HeroSection() {
  const headRef = useRef<HTMLHeadingElement>(null);
  const subRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-[#050507]">
      {/* Full-bleed background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/8533fb5495a3a778dab5f4e151e3e81b.jpg"
          alt=""
          className="w-full h-full object-cover object-center"
          style={{ opacity: 0.45 }}
        />
        {/* Gradient overlays for cinematic depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#050507]/70 via-[#050507]/20 to-[#050507]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050507]/60 via-transparent to-[#050507]/60" />
      </div>

      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      {/* Content */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 pt-36 pb-28 flex flex-col items-center text-center">
        {/* Eyebrow badge */}
        <div
          className="inline-flex items-center gap-2.5 border border-[#4ADE80]/25 rounded-full px-5 py-2 mb-10 backdrop-blur-sm bg-white/[0.03]"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? "translateY(0)" : "translateY(16px)",
            transition: "opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s",
          }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#4ADE80] animate-pulse inline-block" />
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#4ADE80]">
            AI Systems Company
          </span>
        </div>

        {/* Headline — Apple-scale */}
        <h1
          ref={headRef}
          className="font-extrabold text-white leading-[0.95] tracking-[-0.04em] max-w-5xl mb-8"
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "clamp(3.5rem, 8vw, 7.5rem)",
            opacity: loaded ? 1 : 0,
            transform: loaded ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.9s ease 0.25s, transform 0.9s ease 0.25s",
          }}
        >
          Built on Intelligence.{" "}
          <span
            style={{
              background: "linear-gradient(135deg, #4ADE80 0%, #22C55E 50%, #86EFAC 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Designed
          </span>{" "}
          to Last.
        </h1>

        {/* Subheadline */}
        <p
          ref={subRef}
          className="text-white/50 max-w-2xl leading-relaxed mb-12"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "clamp(1rem, 1.5vw, 1.25rem)",
            opacity: loaded ? 1 : 0,
            transform: loaded ? "translateY(0)" : "translateY(24px)",
            transition: "opacity 0.8s ease 0.45s, transform 0.8s ease 0.45s",
          }}
        >
          GroundsWell AI builds structured AI systems that stabilize your operations,
          eliminate inefficiencies, and turn growth into a predictable outcome.
        </p>

        {/* CTAs */}
        <div
          ref={ctaRef}
          className="flex flex-col sm:flex-row items-center gap-4"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 0.8s ease 0.6s, transform 0.8s ease 0.6s",
          }}
        >
          <a
            href="#get-started"
            className="whitespace-nowrap px-9 py-4 rounded-full bg-[#4ADE80] text-[#050507] font-bold text-sm tracking-wide hover:bg-[#22C55E] hover:scale-105 transition-all duration-200 cursor-pointer"
          >
            Build My Foundation
          </a>
          <Link
            to="/services"
            className="whitespace-nowrap px-9 py-4 rounded-full border border-white/20 text-white font-medium text-sm tracking-wide hover:bg-white/5 hover:border-white/35 transition-all duration-200 cursor-pointer backdrop-blur-sm"
          >
            See How It Works &nbsp;↓
          </Link>
        </div>

        {/* Scroll cue */}
        <div
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          style={{
            opacity: loaded ? 0.3 : 0,
            transition: "opacity 1s ease 1.2s",
          }}
        >
          <div className="w-px h-10 bg-gradient-to-b from-white to-transparent" />
        </div>
      </div>
    </section>
  );
}
